import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import { DiagnosisEnergyVectorsReadDTO } from '../../generated/api-client';
import { useDiagnosisEnergyVectorsByDiagnosisId, useVectorTypeOptions } from '../../hooks';

export interface EnergyVectorDropdownProps {
  diagnosisId?: string;
  /** When multiple is true, value is string[]; otherwise string | null */
  value?: string | string[] | null;
  onChange: (
    value: string | string[] | null,
    option: DiagnosisEnergyVectorsReadDTO | DiagnosisEnergyVectorsReadDTO[] | null
  ) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  noOptionsText?: string;
  /** Same as MUI Autocomplete multiple: allow selecting multiple options */
  multiple?: boolean;
  /** Include unit of measurement in option label */
  withUni?: boolean;
}

export function getEnergyVectorOptionLabel(
  option: DiagnosisEnergyVectorsReadDTO,
  vectorTypeOptions: { value: string; label: string }[],
  withUni = true
): string {
  const vectorType = option.vector_type;
  const vectorName = vectorType
    ? (vectorTypeOptions.find(opt => opt.value === vectorType)?.label ?? vectorType)
    : (option.code ?? option.table_code_index ?? '');
  if (!withUni) {
    return vectorName;
  }
  const unit = (option as any).unit_of_measurement ?? '';
  return unit ? `${vectorName} [${unit}]` : vectorName;
}

export const EnergyVectorDropdown: React.FC<EnergyVectorDropdownProps> = ({
  diagnosisId,
  value,
  onChange,
  label = 'Vettore energetico',
  required = false,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  placeholder = 'Seleziona il vettore energetico',
  noOptionsText = 'Nessun vettore energetico disponibile',
  multiple = false,
  withUni = true,
}) => {
  const { data: vectorTypeOptions = [] } = useVectorTypeOptions();
  const {
    data: energyVectors = [],
    isLoading,
    isFetching,
  } = useDiagnosisEnergyVectorsByDiagnosisId(diagnosisId ?? '');

  const loading = isLoading || isFetching;

  const selectedOption = React.useMemo(() => {
    if (multiple) {
      const ids = Array.isArray(value) ? value : value != null ? [value] : [];
      return energyVectors.filter(v => ids.includes(v.id ?? ''));
    }
    if (!value || Array.isArray(value)) return null;
    return energyVectors.find(v => (v.id ?? '') === value) ?? null;
  }, [energyVectors, value, multiple]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent,
      newValue: DiagnosisEnergyVectorsReadDTO | DiagnosisEnergyVectorsReadDTO[] | null
    ) => {
      if (multiple) {
        const arr = Array.isArray(newValue) ? newValue : newValue != null ? [newValue] : [];
        const ids = arr.map(v => v.id ?? '');
        onChange(ids.length ? ids : null, arr.length ? arr : null);
      } else {
        const single = Array.isArray(newValue) ? (newValue[0] ?? null) : newValue;
        onChange(single?.id ?? null, single);
      }
    },
    [multiple, onChange]
  );

  return (
    <Autocomplete<DiagnosisEnergyVectorsReadDTO, boolean>
      multiple={multiple}
      options={energyVectors}
      value={selectedOption as DiagnosisEnergyVectorsReadDTO | DiagnosisEnergyVectorsReadDTO[]}
      onChange={handleChange}
      getOptionLabel={option => getEnergyVectorOptionLabel(option, vectorTypeOptions, withUni)}
      isOptionEqualToValue={(option, val) => (option.id ?? '') === (val?.id ?? '')}
      loading={loading}
      disabled={disabled || !diagnosisId}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText={loading ? 'Caricamento...' : noOptionsText}
      sx={{ width: fullWidth ? '100%' : undefined }}
    />
  );
};
